import { useState, useEffect, useRef } from "react";

function Spinner() {
  return (
    <svg
      className="animate-spin"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

export default function Button({
  children,
  backgroundColor = "bg-gray-100",
  textColor = "text-gray-800",
  className = "",
  as = "button",
  px = "px-4",
  py = "py-2",
  loading = false,
  border = false,
  borderColor = "border-gray-200",
  hover = "hover:bg-gray-200",
  ...props
}) {
  const Component = as;

  const [showSpinner, setShowSpinner] = useState(false);
  const minDuration = 400; // ms
  const timerRef = useRef(null);

  useEffect(
    () => {
      if (loading) {
        setShowSpinner(true);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      } else if (showSpinner) {
        timerRef.current = setTimeout(() => {
          setShowSpinner(false);
          timerRef.current = null;
        }, minDuration);
      }
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    },
    [loading] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const baseClasses = "relative rounded-md font-medium text-sm";
  let combinedClasses =
    `${backgroundColor} ${textColor} ${px} ${py} ${baseClasses} ${className}`.trim();

  if (border) {
    combinedClasses += ` border-[1px] ${borderColor}`;
  }

  if (props.disabled) {
    combinedClasses += " opacity-50";
  } else {
    combinedClasses += ` ${hover}`;
  }

  let innerClasses = "flex items-center justify-center gap-2";
  if (showSpinner) {
    innerClasses += " opacity-0 select-none pointer-events-none";
  }

  return (
    <Component className={combinedClasses} {...props}>
      {showSpinner && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <Spinner />
        </span>
      )}
      <span className={innerClasses}>{children}</span>
    </Component>
  );
}
